import { useState } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import WebsiteDnsPlan from "@/components/dashboard/WebsiteDnsPlan";
import WebsiteDomainRenewal from "@/components/dashboard/WebsiteDomainRenewal";
import WebsiteAddresses from "@/components/dashboard/WebsiteAddresses";

export default function WebsitePublicationControls({ draftId, releases, locked, hasUnsavedChanges, run }) {
  const [hostname, setHostname] = useState("");
  const [status, setStatus] = useState(null);
  const [hosting, setHosting] = useState(null);
  const [releaseId, setReleaseId] = useState("");
  const [history, setHistory] = useState(null);
  const normalizedHost = hostname.trim().toLowerCase().replace(/\.$/, "");
  const selectedRelease = releases.find((item) => item.id === releaseId);
  const statusIsCurrent = status?.hostname === normalizedHost;
  const differentDraft = statusIsCurrent && status.draft_id && status.draft_id !== draftId;
  const statusLabel = status?.status === "active" ? "Published"
    : status?.status === "withdrawn" ? "Offline" : "Not published";

  const refresh = () => run("publication-status", async () => {
    const { data } = await axios.get(
      API + "/website-builder/publications/" + encodeURIComponent(normalizedHost),
    );
    setStatus(data);
  });

  const loadHistory = () => run("publication-history", async () => {
    const { data } = await axios.get(
      API + "/website-builder/publications/" + encodeURIComponent(normalizedHost) + "/history",
    );
    setHistory(data);
  });

  const reservePreview = () => {
    if (locked) return;
    if (!window.confirm("Reserve a Zynthoro preview address for this saved website? This does not publish it.")) return;
    return run("preview-reservation", async () => {
      const { data } = await axios.post(API + "/website-builder/publications/hosting/preview", {
        draft_id: draftId,
      });
      setHostname(data.hostname); setHosting(data); setStatus(null); setHistory(null);
    });
  };

  const checkHosting = () => run("hosting-status", async () => {
    const { data } = await axios.get(
      API + "/website-builder/publications/hosting/" + encodeURIComponent(normalizedHost),
    );
    setHosting(data);
  });

  const requestHosting = () => {
    if (locked || !normalizedHost) return;
    if (!window.confirm("Record a hosting setup request for " + normalizedHost +
      "? This does not publish your website or change DNS.")) return;
    return run("hosting-request", async () => {
      const { data } = await axios.post(API + "/website-builder/publications/hosting", {
        hostname: normalizedHost,
      });
      setHosting(data);
    });
  };

  const publish = () => {
    if (locked || !statusIsCurrent || !selectedRelease || hasUnsavedChanges || differentDraft) return;
    const created = new Date(selectedRelease.created_at).toLocaleString();
    if (!window.confirm(
      "Publish the saved release from " + created + " on https://" + normalizedHost +
      "? Visitors will see this release. It replaces any currently published version; it does not publish unsaved editor changes.",
    )) return;
    return run("publish", async () => {
      const { data } = await axios.post(API + "/website-builder/publications", {
        hostname: normalizedHost, release_id: releaseId,
        expected_revision: status.revision, confirm_publication: true,
      });
      setStatus(data); setHistory(null);
    });
  };

  const withdraw = () => {
    if (locked || !statusIsCurrent || status.status !== "active" || differentDraft) return;
    if (!window.confirm("Take https://" + normalizedHost + " offline? Your draft and saved releases will be kept.")) return;
    return run("withdraw", async () => {
      const { data } = await axios.post(API + "/website-builder/publications/withdraw", {
        hostname: normalizedHost, expected_revision: status.revision, confirm_withdrawal: true,
      });
      setStatus(data); setHistory(null);
    });
  };

  return (
    <div className="space-y-3 border-t pt-3">
      <h3 className="text-sm font-semibold">Website publication</h3>
      <WebsiteAddresses draftId={draftId} locked={locked} run={run}
        onSelect={(address) => {
          setHostname(address); setStatus(null); setHosting(null); setHistory(null);
        }} />
      <p className="text-xs text-slate-500">Publication requires verified domain ownership, working routing and HTTPS. It stays unavailable until hosting setup is complete.</p>
      <button type="button" disabled={locked} onClick={reservePreview}
        className="rounded border px-3 py-2 text-sm disabled:opacity-50">Reserve Zynthoro preview address</button>
      <p className="text-xs text-slate-500">A reserved address stays unpublished until hosting is ready and you explicitly publish a saved release. A published preview is publicly accessible.</p>
      <label className="block text-sm">Website hostname
        <input type="text" value={hostname} maxLength={253} placeholder="www.example.com"
          disabled={locked} autoCapitalize="none" autoCorrect="off" spellCheck={false}
          onChange={(event) => { setHostname(event.target.value); setStatus(null); setHosting(null); setHistory(null); }}
          className="mt-1 block w-full rounded border p-2 disabled:opacity-50" />
      </label>
      <button type="button" disabled={locked || !normalizedHost} onClick={refresh}
        className="rounded border px-3 py-2 text-sm disabled:opacity-50">Check publication status</button>
      <div className="space-y-2 rounded border p-3">
        <h4 className="text-sm font-medium">Hosting preparation</h4>
        <p className="text-xs text-slate-500">Existing domains need ownership verification in Domains. Purchased domains need registrar confirmation, followed by the DNS records shown after a setup request. Root and www addresses are checked separately. A request does not publish a website.</p>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={locked || !normalizedHost} onClick={checkHosting}
            className="rounded border px-3 py-2 text-sm disabled:opacity-50">Check hosting setup</button>
          <button type="button" disabled={locked || !normalizedHost} onClick={requestHosting}
            className="rounded border px-3 py-2 text-sm disabled:opacity-50">Request hosting preparation</button>
        </div>
        {hosting?.hostname === normalizedHost && <div role="status" className="space-y-1 text-xs text-slate-600">
          <p>{({
            not_requested: "No hosting request recorded.",
            requested: "Request recorded; provisioning is still pending.",
            checking_dns: "Checking domain ownership and routing.",
            dns_ready: "DNS checks passed; HTTPS and website origin preparation are still pending.",
            checking_https: "Checking HTTPS and the Zynthoro website origin.",
            tls_error: "HTTPS or website origin verification has not passed. A later controller attempt is required.",
            ready: "Hosting checks passed. Publishing still requires your confirmation.",
            verification_due: "Hosting verification needs refreshing before this website can be served or published.",
            dns_wait: "Ownership or routing records do not match yet. Review the DNS records in Domains.",
            dns_error: "DNS could not be checked. A later controller attempt is required.",
          })[hosting.status] || "Hosting preparation is in progress."}</p>
          <p>Ownership: {hosting.ownership_verified ? "Verified" : "Pending"} · Routing: {hosting.routing_verified ? "Verified" : "Pending"}</p>
          <p>HTTPS: {hosting.tls_status === "active" ? "Ready" : "Pending"} · Website origin: {hosting.origin_ready ? "Ready" : "Pending"}</p>
        </div>}
      </div>
      {hosting?.hostname === normalizedHost && hosting.dns_records && (
        <div className="space-y-2 rounded border p-3">
          <h4 className="text-sm font-medium">DNS records for your purchased domain</h4>
          <p className="text-xs text-slate-500">In the DNS settings for {hosting.dns_records.domain}, add these records. This screen does not change DNS. Keep existing email records.</p>
          {[hosting.dns_records.ownership, hosting.dns_records.routing].map((record) => (
            <div key={record.type + record.host} className="space-y-1 rounded bg-slate-50 p-2 text-xs">
              <p>{record.type} · Host: {record.host}</p>
              <p className="break-all select-all font-mono">{record.value}</p>
            </div>
          ))}
          <p className="text-xs text-slate-500">The controller must observe the ownership and routing records before HTTPS can be verified.</p>
          <WebsiteDnsPlan key={normalizedHost + ":" + hosting.dns_records.ownership.value}
            hostname={normalizedHost} locked={locked} run={run} />
          <WebsiteDomainRenewal key={normalizedHost} hostname={normalizedHost} locked={locked} run={run} />
        </div>
      )}
      {statusIsCurrent && <p role="status" className="text-sm">
        {normalizedHost}: {statusLabel}
      </p>}
      {differentDraft && <p role="alert" className="text-sm text-amber-700">
        This hostname is assigned to a different website. Open that website to manage its publication.
      </p>}
      {releases.length > 0 ? <label className="block text-sm">Saved release to publish
        <select value={releaseId} disabled={locked} onChange={(event) => setReleaseId(event.target.value)}
          className="mt-1 block w-full rounded border p-2">
          <option value="">Choose a saved release</option>
          {releases.map((release) => <option key={release.id} value={release.id}>
            {new Date(release.created_at).toLocaleString()}
            {statusIsCurrent && status.status === "active" && status.active_release_id === release.id ? " · Currently published" : ""}
          </option>)}
        </select>
      </label> : <p className="text-xs text-slate-500">Store a private release or show saved releases first.</p>}
      <p className="text-xs text-slate-500">To return to an earlier version, choose its saved release and publish it. Your current draft stays unchanged. External images remain linked to their original source.</p>
      {hasUnsavedChanges && <p className="text-xs text-amber-700">Save editor changes before publishing a release.</p>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={locked || !statusIsCurrent || !selectedRelease || hasUnsavedChanges || !!differentDraft}
          onClick={publish} className="rounded border px-3 py-2 text-sm disabled:opacity-50">
          Review and publish release
        </button>
        <button type="button" disabled={locked || !statusIsCurrent || status?.status !== "active" || !!differentDraft}
          onClick={withdraw} className="rounded border px-3 py-2 text-sm disabled:opacity-50">
          Take website offline
        </button>
      </div>
      <div className="space-y-2 border-t pt-3">
        <h4 className="text-sm font-medium">Publication history</h4>
        <button type="button" disabled={locked || !normalizedHost || !!differentDraft} onClick={loadHistory}
          className="rounded border px-3 py-2 text-sm disabled:opacity-50">Show recent publication history</button>
        <p className="text-xs text-slate-500">Up to 50 recent actions. Selecting a version does not publish it.</p>
        {history?.hostname === normalizedHost && history.draft_id && history.draft_id !== draftId
          ? <p className="text-xs text-amber-700">This hostname belongs to another website. Open that website to manage its versions.</p>
          : history?.hostname === normalizedHost && <ul className="space-y-2">
            {history.events.length === 0 && <li className="text-xs text-slate-500">No publication history recorded.</li>}
            {history.events.map((event, index) => (
              <li key={event.at + "-" + index} className="flex flex-wrap items-center justify-between gap-2 rounded border p-2 text-xs">
                <span>{event.action === "activate" ? "Release published" : "Website taken offline"} · {new Date(event.at).toLocaleString()}</span>
                {event.action === "activate" && releases.some((release) => release.id === event.release_id)
                  && <button type="button" disabled={locked} onClick={() => setReleaseId(event.release_id)}
                    className="rounded border px-2 py-1 disabled:opacity-50">
                    {releaseId === event.release_id ? "Selected" : "Select this version"}
                  </button>}
              </li>
            ))}
          </ul>}
      </div>
    </div>
  );
}
